import { useEffect } from 'react';
import '@/styles/silk-confirm-modal.css';
import { useLocale } from '@/context/LocaleContext';

export type SilkConfirmPhase = 'confirm' | 'pending' | 'success' | 'error';

export interface SilkConfirmModalProps {
  open: boolean;
  phase: SilkConfirmPhase;
  title: string;
  message: string;
  cost: number;
  balance: number | null;
  balanceLoading?: boolean;
  balanceError?: boolean;
  resultMessage?: string;
  confirmLabel?: string;
  className?: string;
  onConfirm: () => void;
  onClose: () => void;
}

export function SilkConfirmModal({
  open,
  phase,
  title,
  message,
  cost,
  balance,
  balanceLoading = false,
  balanceError = false,
  resultMessage,
  confirmLabel,
  className,
  onConfirm,
  onClose,
}: SilkConfirmModalProps) {
  const { t } = useLocale();

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && phase !== 'pending') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, phase]);

  if (!open) return null;

  const isConfirm = phase === 'confirm';
  const isPending = phase === 'pending';
  const isSuccess = phase === 'success';
  const isError = phase === 'error';
  const canAfford = balance !== null && balance >= cost;
  const canConfirm = isConfirm && !balanceLoading && !balanceError && canAfford;

  const bodyMessage = isPending
    ? resultMessage || t('silk_confirm_processing_msg')
    : isSuccess || isError
      ? resultMessage || message
      : message;

  const headerTitle = isPending
    ? t('silk_confirm_processing')
    : isSuccess
      ? t('silk_confirm_success')
      : isError
        ? t('silk_confirm_failed')
        : title;

  return (
    <div
      className={['reset-spin-modal', 'silk-confirm-modal', className].filter(Boolean).join(' ')}
      role="dialog"
      aria-modal
      onClick={isPending ? undefined : onClose}
      onKeyDown={() => {}}
    >
      <div className="reset-spin-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="reset-spin-modal-header">
          <h3>{headerTitle}</h3>
        </div>

        <div className="reset-spin-modal-body">
          {isPending && <div className="silk-confirm-spinner" aria-hidden />}

          {isSuccess && (
            <p>
              <i className="ph-light ph-check-circle silk-confirm-status-icon silk-confirm-status-icon--success" aria-hidden />
            </p>
          )}
          {isError && (
            <p>
              <i className="ph-light ph-x-circle silk-confirm-status-icon silk-confirm-status-icon--error" aria-hidden />
            </p>
          )}

          <p>{bodyMessage}</p>

          {isConfirm && (
            <div className="silk-confirm-details">
              <p className="reset-spin-silk-amount">
                <strong>{t('silk_confirm_cost', { amount: cost.toLocaleString() })}</strong>
              </p>
              <p className="silk-confirm-balance">
                {balanceLoading ? (
                  <span
                    className="silk-confirm-skeleton-line silk-confirm-skeleton-line--wide"
                    aria-label={t('silk_confirm_checking')}
                  />
                ) : balanceError ? (
                  t('silk_confirm_unavailable')
                ) : balance !== null ? (
                  t('silk_confirm_balance', { amount: balance.toLocaleString() })
                ) : (
                  <span
                    className="silk-confirm-skeleton-line silk-confirm-skeleton-line--wide"
                    aria-hidden
                  />
                )}
              </p>
              <p
                className={`silk-confirm-after${
                  !balanceLoading && !balanceError && balance !== null && !canAfford
                    ? ' silk-confirm-after--low'
                    : ''
                }`}
              >
                {balanceLoading || balance === null ? (
                  <span
                    className="silk-confirm-skeleton-line silk-confirm-skeleton-line--medium"
                    aria-hidden
                  />
                ) : balanceError ? (
                  '\u00A0'
                ) : (
                  t('silk_confirm_after', {
                    amount: Math.max(0, balance - cost).toLocaleString(),
                  })
                )}
              </p>
              <p
                className={`silk-confirm-warning silk-confirm-warning--slot${
                  !canAfford && !balanceLoading && !balanceError && balance !== null
                    ? ' silk-confirm-warning--visible'
                    : ''
                }`}
              >
                {!canAfford && !balanceLoading && !balanceError && balance !== null
                  ? t('silk_confirm_not_enough')
                  : '\u00A0'}
              </p>
            </div>
          )}
        </div>

        <div className="reset-spin-modal-footer">
          {isConfirm ? (
            <>
              <button type="button" className="reset-spin-btn-cancel" onClick={onClose}>
                {t('silk_confirm_cancel')}
              </button>
              <button
                type="button"
                className="reset-spin-btn-confirm"
                disabled={!canConfirm}
                onClick={onConfirm}
              >
                {balanceError
                  ? t('silk_confirm_retry_later')
                  : !canAfford && !balanceLoading && balance !== null
                    ? t('silk_confirm_not_enough_short')
                    : (confirmLabel ?? t('silk_confirm_confirm'))}
              </button>
            </>
          ) : (
            <button
              type="button"
              className="reset-spin-btn-confirm silk-confirm-btn-full"
              disabled={isPending}
              onClick={onClose}
            >
              {isPending ? t('silk_confirm_wait') : t('silk_confirm_close')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
